<script setup>
console.log('🔥 AI Literacy Student Component Script 로드됨');

import { ref, onMounted, provide, nextTick, shallowRef } from 'vue';
import { useRoute } from 'vue-router';
import { downloadResultPDF } from '@/utils/pdfDownloader.js';
import api from '@/lib/api.js';
import PageLayout from '@/components/result/ai-literacy-2026/PageLayout.vue';
import PdfDownloadProgress from '@/components/result/ai-literacy-2026/PdfDownloadProgress.vue';
import Page1 from './Page1.vue';
import Page2 from './Page2.vue';
import Page3 from './Page3.vue';
import Page4 from './Page4.vue';
import Page5 from './Page5.vue';

console.log('📦 모든 imports 완료');

// shallowRef로 컴포넌트 배열을 더 안전하게 관리
const pages = shallowRef([
  { name: 'Page1', component: Page1 },
  { name: 'Page2', component: Page2 },
  { name: 'Page3', component: Page3 },
  { name: 'Page4', component: Page4 },
  { name: 'Page5', component: Page5 }
]);

const route = useRoute();

console.log('🌍 setup 함수 실행 중, 현재 라우트:', route.path, route.query);

// Props로 받은 학생 데이터 (일괄 다운로드용)
const props = defineProps({
  studentData: {
    type: Object,
    default: null
  }
});

// AI 리터러시 결과 데이터
const studentData = ref(props.studentData || null);
const loading = ref(!props.studentData); // props가 있으면 로딩하지 않음
const pdfDownloading = ref(false);
const downloadingDots = ref('.');
const pdfProgress = ref(null); // PDF 생성 진행률 (PdfDownloadProgress 에 표시)
let dotsInterval = null;

// API 호출 함수
const fetchStudentResult = async () => {
  try {
    console.log('🚀 fetchStudentResult 시작');
    loading.value = true;
    const examId = route.query.exam_id;
    const userId = route.query.user_id || studentData.value?.user_id;
    
    if (!examId) {
      throw new Error('exam_id가 없습니다.');
    }

    // API 요청 파라미터 구성
    const requestData = {
      exam_id: examId
    };
    
    // user_id가 있는 경우 추가
    if (userId) {
      requestData.user_id = userId;
    }
    
    const response = await api.post('/exam/stu/resultLitDetail', requestData);
    


    if (response.data.success) {
      // nextTick을 사용하여 데이터 업데이트를 더 안전하게 처리
      await nextTick();
      studentData.value = response.data.result;
    } else {
      console.error('❌ API 응답 실패:', response.data);
      throw new Error(response.data.message || '데이터를 가져오는데 실패했습니다.');
    }
  } catch (error) {
    console.error('💥 AI 리터러시 학생 결과 데이터 조회 실패:', error);
    console.error('💥 에러 상세:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
  } finally {
    loading.value = false;
  }
};

// 하위 컴포넌트에 데이터 전달
provide('studentData', studentData);
provide('loading', loading);

const subTitle = "학생 진단검사 결과지";

// PDF 다운로드 함수
const triggerReportDownload = async (param = null) => {
  //개인다운로드 / 일괄다운로드 구분 하기
  const isClickEvent = param && typeof param === 'object' && param.type === 'click';
  const uniqueUserId = isClickEvent ? null : param; // 클릭 이벤트면 null, 아니면 user_i
  
  
  try {
    // PDF 다운로드 로딩 상태 시작
    pdfDownloading.value = true;
    
    // 점 애니메이션 시작
    dotsInterval = setInterval(() => {
      if (downloadingDots.value === '.') {
        downloadingDots.value = '..';
      } else if (downloadingDots.value === '..') {
        downloadingDots.value = '...';
      } else {
        downloadingDots.value = '.';
      }
    }, 500);
    
    console.log('PDF 생성 시작...');
    
    // 컨테이너 선택자 결정: 개인 다운로드는 기존 방식, 일괄 다운로드만 유니크 선택자
    let containerSelector = '.pdf-container';
    if (uniqueUserId) {
      // 일괄 다운로드용 유니크 선택자
      containerSelector = `.hidden-student-${uniqueUserId} .pdf-container`;
    }
    
    const pdfContainer = document.querySelector(containerSelector);
    const pageLayouts = document.querySelectorAll(`${containerSelector} .page-layout`);
    
    if (!pdfContainer) {
      throw new Error(`PDF 컨테이너를 찾을 수 없습니다: ${containerSelector}`);
    }
    
    if (pageLayouts.length === 0) {
      throw new Error(`페이지 레이아웃 요소를 찾을 수 없습니다: ${containerSelector} .page-layout`);
    }
    
    // 파일명 생성
    let filename = '';
    if (studentData.value && (studentData.value.user_nm || studentData.value.name)) {
      const studentName = studentData.value.user_nm || studentData.value.name;
      const schoolName = studentData.value.schul_nm || '학교';
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
      filename = `${schoolName}_${studentName}_AI리터러시_검사결과_${dateStr}.pdf`;
    } else {
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
      filename = `AI리터러시_검사결과지_${dateStr}.pdf`;
    }
    
    // PDF 생성 진행률 콜백
    const onProgress = (progress) => {
      pdfProgress.value = progress;
    };
    
    // PDF 생성 및 다운로드 - 개별/일괄 모두 지원
    const result = await downloadResultPDF({
      filename: filename,
      onProgress: onProgress,
      containerSelector: containerSelector,
      pageSelector: '.page-layout'
    });
    
    console.log('PDF 다운로드 완료:', result);
    
  } catch (error) {
    console.error('PDF 다운로드 실패:', error);
    alert('PDF 생성 중 오류가 발생했습니다. 다시 시도해주세요.');
  } finally {
    // PDF 다운로드 로딩 상태 종료
    pdfDownloading.value = false;
    
    // 점 애니메이션 중지
    if (dotsInterval) {
      clearInterval(dotsInterval);
      dotsInterval = null;
    }
    downloadingDots.value = '.';
    pdfProgress.value = null;
  }
};

// 컴포넌트 마운트 시 데이터 로드
onMounted(() => {
  // Props로 데이터를 받은 경우 (일괄 다운로드)
  if (props.studentData) {
    studentData.value = props.studentData;
    loading.value = false;
    return;
  }
  fetchStudentResult();
});

// 외부에서 triggerReportDownload 함수를 호출할 수 있도록 expose
defineExpose({
  triggerReportDownload
});
</script>

<template>
  <div class="top-area pt-5">
    <div class="loading">                 
        <div class="loading-overlay">
            <div class="loading-content">
                <div class="top-text pb-4">
                    <h4>검사결과지 다운로드 ({{pages.length}}페이지)</h4> 
                    <p class="sub-message pt-2">학생들의 리터러시 척도점수와 영역별 도달도 등 평균적인 분석 결과와 대시보드를 제공합니다.</p>    
                </div>                    
                <button @click="triggerReportDownload" class="download-button" :disabled="pdfDownloading">
                    <span v-if="pdfDownloading">📥 다운로드 중{{ downloadingDots }}</span>
                    <span v-else>검사결과지 PDF 다운로드</span>
                </button>
                <PdfDownloadProgress v-if="pdfDownloading" :progress="pdfProgress" />
            </div>
        </div>  
    </div>
  </div> 

  <!-- 로딩 중일 때 -->
  <div v-if="loading" class="text-center p-10">
    <p>📡 데이터를 불러오는 중...</p>
    <p class="text-sm text-gray-500 mt-2">exam_id: {{ route.query.exam_id }}</p>
  </div>

  <!-- 데이터 로드 완료 후 페이지 렌더링 -->
  <div v-else-if="studentData && !loading" class="pdf-container">
    <template v-for="(pageItem, index) in pages" :key="`page-${pageItem.name}-${index}`">
      <PageLayout 
        class="page-layout"
        :current-page="index + 1"
        :total-pages="pages.length"
        :sub-title="subTitle">
        <KeepAlive>
          <component 
            :is="pageItem.component" 
            :studentData="studentData"
            :key="`${pageItem.name}-${studentData.exam_id || 'default'}`"
          />
        </KeepAlive>
      </PageLayout>
    </template>
  </div>

  <!-- 데이터 로드 실패 시 -->
  <div v-else class="text-center p-10">
    <p>❌ 데이터를 불러올 수 없습니다.</p>
    <p class="text-sm text-gray-500 mt-2">exam_id: {{ route.query.exam_id }}</p>
    <p class="text-sm text-gray-500">user_id: {{ route.query.user_id }}</p>
  </div>
</template>

<style lang="scss">
.top-area {
    width: 1130px ;
    margin: 0 auto;   
    position: relative;
    z-index: 20;
}

.loading {
    .loading-overlay {
        width: 1130px ;        
        padding: 25px 0 30px;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        background-color: #fff;             
        border-radius: 15px; 
        font-size: 18px;        
    }
}

.top-text {
    font-weight: 500;
    line-height: 1.4;
}

.download-button {
    color: #fff;
    background-color: #247CFF;
    border-radius: 5px;
    padding: 4px 16px;
    cursor: pointer;
    border: none;
    
    &:disabled {
        background-color: #ccc;
        cursor: not-allowed;
        opacity: 0.7;
    }
}

// PDF 전용 스타일 - scoped 제거로 일괄 다운로드에서도 CSS 상속 보장
.pdf-container {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 0;
    background-color: white;
    // 명시적인 폰트 크기 설정 제거 - 브라우저 기본값과 상속에 의존
}

.page-layout {
    width: 1130px;
    min-height: 800px;
    border: 1px solid #ddd;
    border-bottom: 2px solid #333; /* 페이지 구분을 위한 굵은 테두리 */
    display: flex;
    flex-direction: column;
    background: white;
    box-sizing: border-box;
    padding: 20px;
    margin-bottom: 40px; /* 페이지 간격 */
    
    // PDF 생성 시 각 페이지를 독립 페이지로 처리하도록 강제
    &::after {
        content: "";
        display: block;
        height: 50px;
        border-bottom: 3px solid #000; /* 강제 페이지 구분선 */
        margin: 20px 0;
    }
    
    &:last-child {
        border-bottom: 1px solid #ddd;
        margin-bottom: 0;
        
        &::after {
            display: none;
        }
    }
}
</style>